/**
 * Official DIGIPIN National Level Spatial Addressing System Encoder & Decoder
 * Developed by CEPT, Department of Posts, India.
 *
 * Converts WGS84 (latitude, longitude) coordinates into a 10-character DIGIPIN 4m Geo-Grid code
 * and decodes a DIGIPIN code back into WGS84 central latitude & longitude coordinates.
 */

export interface DecodedDigipin {
  latitude: number;
  longitude: number;
  formatted: string;
  rawCode: string;
}

const PRIMARY_GRID = [
  ['F', 'C', '9', '8'],
  ['J', '3', '2', '7'],
  ['K', '4', '5', '6'],
  ['L', 'M', 'P', 'T'],
];

const PRIMARY_BOUNDS = {
  minLat: 2.5,
  maxLat: 38.5,
  minLon: 63.5,
  maxLon: 99.5,
};

const DRAFT_GRID = [
  ['2', '3', '4', '5'],
  ['6', '7', '8', '9'],
  ['C', 'J', 'K', 'L'],
  ['M', 'P', 'T', 'W'],
];

const DRAFT_BOUNDS = {
  minLat: 2.0,
  maxLat: 38.0,
  minLon: 68.0,
  maxLon: 98.0,
};

/**
 * Checks if a string looks like a valid 10-character DIGIPIN code.
 * Accepts with or without hyphens (e.g. "3J8-K82-L914", "4T396F42L7").
 */
export function looksLikeDigipin(input: string): boolean {
  if (!input) return false;
  const clean = input.replace(/[\s-]/g, '').toUpperCase();
  if (clean.length !== 10) return false;
  return /^[23456789CJKLMPFTW]{10}$/.test(clean);
}

/**
 * Formats a 10-character DIGIPIN string into standard XXX-XXX-XXXX format.
 */
export function formatDigipin(code: string): string {
  const clean = code.replace(/[\s-]/g, '').toUpperCase();
  if (clean.length !== 10) return code;
  return `${clean.slice(0, 3)}-${clean.slice(3, 6)}-${clean.slice(6)}`;
}

/**
 * Encodes latitude and longitude coordinates into a 10-character DIGIPIN code.
 * @returns Formatted DIGIPIN string (e.g. "4T3-96F-42L7") or null if outside India bounds.
 */
export function encodeDigipin(lat: number, lon: number): string | null {
  if (
    typeof lat !== 'number' ||
    typeof lon !== 'number' ||
    isNaN(lat) ||
    isNaN(lon) ||
    lat < PRIMARY_BOUNDS.minLat ||
    lat > PRIMARY_BOUNDS.maxLat ||
    lon < PRIMARY_BOUNDS.minLon ||
    lon > PRIMARY_BOUNDS.maxLon
  ) {
    return null;
  }

  let minLat = PRIMARY_BOUNDS.minLat;
  let maxLat = PRIMARY_BOUNDS.maxLat;
  let minLon = PRIMARY_BOUNDS.minLon;
  let maxLon = PRIMARY_BOUNDS.maxLon;

  let code = '';

  for (let level = 0; level < 10; level++) {
    const latDiv = (maxLat - minLat) / 4;
    const lonDiv = (maxLon - minLon) / 4;

    let row = 3 - Math.floor((lat - minLat) / latDiv);
    let col = Math.floor((lon - minLon) / lonDiv);

    row = Math.max(0, Math.min(row, 3));
    col = Math.max(0, Math.min(col, 3));

    code += PRIMARY_GRID[row][col];

    maxLat = minLat + latDiv * (4 - row);
    minLat = minLat + latDiv * (3 - row);
    minLon = minLon + lonDiv * col;
    maxLon = minLon + lonDiv;
  }

  return formatDigipin(code);
}

/**
 * Decodes a 10-character DIGIPIN code back to its central latitude and longitude.
 * @returns DecodedDigipin object or null if invalid format.
 */
export function decodeDigipin(digiPin: string): DecodedDigipin | null {
  if (typeof digiPin !== 'string') return null;
  const pin = digiPin.replace(/[\s-]/g, '').toUpperCase();

  if (pin.length !== 10 || !/^[23456789CJKLMPFTW]{10}$/.test(pin)) {
    return null;
  }

  const isDraft = pin.includes('W') && !pin.includes('F');
  const grid = isDraft ? DRAFT_GRID : PRIMARY_GRID;
  const bounds = isDraft ? DRAFT_BOUNDS : PRIMARY_BOUNDS;

  let minLat = bounds.minLat;
  let maxLat = bounds.maxLat;
  let minLon = bounds.minLon;
  let maxLon = bounds.maxLon;

  for (let i = 0; i < 10; i++) {
    const char = pin[i];
    let ri = -1;
    let ci = -1;

    for (let r = 0; r < 4; r++) {
      for (let c = 0; c < 4; c++) {
        if (grid[r][c] === char) {
          ri = r;
          ci = c;
          break;
        }
      }
      if (ri !== -1) break;
    }

    if (ri === -1 || ci === -1) return null;

    const latDiv = (maxLat - minLat) / 4;
    const lonDiv = (maxLon - minLon) / 4;

    if (isDraft) {
      const row = 3 - ri;
      minLat = minLat + row * latDiv;
      maxLat = minLat + latDiv;
      minLon = minLon + ci * lonDiv;
      maxLon = minLon + lonDiv;
    } else {
      const lat1 = maxLat - latDiv * (ri + 1);
      const lat2 = maxLat - latDiv * ri;
      const lon1 = minLon + lonDiv * ci;
      const lon2 = minLon + lonDiv * (ci + 1);

      minLat = lat1;
      maxLat = lat2;
      minLon = lon1;
      maxLon = lon2;
    }
  }

  const centerLat = Number(((minLat + maxLat) / 2).toFixed(6));
  const centerLon = Number(((minLon + maxLon) / 2).toFixed(6));

  return {
    latitude: centerLat,
    longitude: centerLon,
    formatted: formatDigipin(pin),
    rawCode: pin,
  };
}
